import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { IconDashboard, IconSuche, IconAnalytics, IconDokumente } from './MobileIcons';
import { NavStelleCard } from './MobileCards';
import { parseMatchScore } from '../utils/themeUtils';

const NAV_ITEMS = [
  { id: 'dashboard',  label: 'Dashboard',       Icon: IconDashboard },
  { id: 'suche',      label: 'Suche',           Icon: IconSuche },
  { id: 'analytics',  label: 'Analytics',       Icon: IconAnalytics },
  { id: 'dokumente',  label: 'Meine Dokumente', Icon: IconDokumente },
];
const MENU_ITEM = (danger = false) => ({
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '13px 22px', borderRadius: 11,
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 16, fontWeight: 500, textAlign: 'left',
  fontFamily: 'var(--font-ui)',
  color: danger ? 'var(--clr-error)' : 'var(--cv-text)',
  WebkitTapHighlightColor: 'transparent',
});

const CONTEXT_MENU_WIDTH = 242;
const CONTEXT_MENU_MAX_HEIGHT = 256;
const CONTEXT_MENU_MARGIN = 12;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function NavIsland({ stellen, activeNav, onNavSelect, markierteIds, onMarkierenToggle, onStelleAuswaehlen, aktiveStelleId, th, onDelete, onRename, rolle }) {
  const [ctxMenu, setCtxMenu] = useState(null); // { stelle, top, left }
  const [renamingId, setRenamingId] = useState(null);

  const istAusstehend = (s) => !s.generiert_am && !s.analyse;

  const sortiert = [...stellen].filter(s => s.quelle !== 'extern').sort((a, b) => {
    const aOut = istAusstehend(a) ? 1 : 0;
    const bOut = istAusstehend(b) ? 1 : 0;
    if (aOut !== bOut) return aOut - bOut;
    const ta = a.erstellt_am ? new Date(a.erstellt_am).getTime() : 0;
    const tb = b.erstellt_am ? new Date(b.erstellt_am).getTime() : 0;
    return tb - ta;
  });

  const markierte = sortiert.filter(s => markierteIds.has(s.id));
  const restliche = sortiert.filter(s => !markierteIds.has(s.id)).slice(0, 8);
  const openCtx = (stelle, rect) => {
    const maxLeft = window.innerWidth - CONTEXT_MENU_WIDTH - CONTEXT_MENU_MARGIN;
    const maxTop = window.innerHeight - CONTEXT_MENU_MAX_HEIGHT - CONTEXT_MENU_MARGIN;
    const preferredLeft = rect.right - CONTEXT_MENU_WIDTH - 4;
    const preferredTop = rect.top - 10;

    setCtxMenu({
      stelle,
      left: clamp(preferredLeft, CONTEXT_MENU_MARGIN, Math.max(CONTEXT_MENU_MARGIN, maxLeft)),
      top: clamp(preferredTop, CONTEXT_MENU_MARGIN, Math.max(CONTEXT_MENU_MARGIN, maxTop)),
    });
  };

  const closeCtx = () => setCtxMenu(null);

  return (
    <div style={{
      background: th.bgGrad,
      display: 'flex', flexDirection: 'column',
      width: '100%', height: '100%',
      position: 'relative',
    }}>

      {/* ── Dim overlay + Context menu ── */}
      {ctxMenu && ReactDOM.createPortal((() => {
        const cs = ctxMenu.stelle;
        const csAusstehend = !cs.generiert_am && !cs.analyse;
        const csMatch = csAusstehend ? null : (parseMatchScore(cs.analyse) ?? cs.match_score ?? null);
        const csMatchColor = csMatch === null ? th.accent
          : csMatch >= 75 ? '#22a06b'
          : csMatch >= 50 ? th.accent
          : '#e2812d';
        return (
          <>
            <div
              style={{
                position: 'fixed', inset: 0,
                background: 'rgba(28,24,18,0.34)',
                zIndex: 1298,
              }}
              onTouchStart={closeCtx}
              onMouseDown={closeCtx}
            />
            <div
              style={{
                position: 'fixed',
                top: ctxMenu.top,
                left: ctxMenu.left,
                width: CONTEXT_MENU_WIDTH,
                maxWidth: 'calc(100vw - 24px)',
                background: 'var(--cv-bg-surface)',
                border: '1px solid var(--cv-border)',
                borderRadius: 18,
                boxShadow: '0 22px 48px rgba(28,24,18,0.22), 0 4px 14px rgba(28,24,18,0.10)',
                padding: '10px 0',
                zIndex: 1299,
                animation: 'dropdownIn 0.15s ease',
                overflow: 'hidden',
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Stelle-Vorschau */}
              <div style={{ padding: '12px 22px 12px' }}>
                <div style={{
                  fontSize: 16, fontWeight: 700, color: 'var(--cv-text)',
                  letterSpacing: 0, lineHeight: 1.25,
                  fontFamily: 'var(--font-ui)',
                  marginBottom: csMatch !== null ? 10 : 0,
                  overflowWrap: 'anywhere',
                }}>
                  {cs.name || 'Stelle'}
                </div>
                {csMatch !== null && (
                  <>
                    <div style={{
                      height: 4, borderRadius: 999,
                      background: 'var(--cv-border)', overflow: 'hidden', marginBottom: 5,
                    }}>
                      <div style={{
                        height: '100%', width: `${csMatch}%`,
                        background: csMatchColor, borderRadius: 999,
                        transition: 'width 0.4s ease',
                      }} />
                    </div>
                    <div style={{
                      fontSize: 12, fontFamily: 'var(--font-ui)',
                      color: csMatchColor, fontWeight: 700,
                    }}>
                      {csMatch}% Übereinstimmung
                    </div>
                  </>
                )}
                {csAusstehend && (
                  <div style={{ fontSize: 12, color: 'var(--clr-error-dark)', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>
                    Analyse ausstehend
                  </div>
                )}
              </div>
              <div style={{ height: 1, background: 'var(--cv-border)', margin: '0 22px 4px' }} />

              {/* Markieren */}
              <button
                style={MENU_ITEM(false)}
                onClick={() => { onMarkierenToggle(ctxMenu.stelle.id); closeCtx(); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke={markierteIds.has(ctxMenu.stelle.id) ? 'var(--cv-amber)' : 'var(--cv-text)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 2.43945V12.4195C17 14.3895 15.59 15.1595 13.86 14.1195L12.54 13.3295C12.24 13.1495 11.76 13.1495 11.46 13.3295L10.14 14.1195C8.41 15.1495 7 14.3895 7 12.4195V2.43945" stroke={markierteIds.has(ctxMenu.stelle.id) ? 'var(--cv-amber)' : 'var(--cv-text)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {markierteIds.has(ctxMenu.stelle.id) ? 'Markierung entfernen' : 'Markieren'}
              </button>

              {/* Umbenennen */}
              <button
                style={MENU_ITEM(false)}
                onClick={() => { setRenamingId(ctxMenu.stelle.id); closeCtx(); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.53999 19.5196C4.92999 19.5196 4.35999 19.3096 3.94999 18.9196C3.42999 18.4296 3.17999 17.6896 3.26999 16.8896L3.63999 13.6496C3.70999 13.0396 4.07999 12.2296 4.50999 11.7896L12.72 3.09956C14.77 0.929561 16.91 0.869561 19.08 2.91956C21.25 4.96956 21.31 7.10956 19.26 9.27956L11.05 17.9696C10.63 18.4196 9.84999 18.8396 9.23999 18.9396L6.01999 19.4896C5.84999 19.4996 5.69999 19.5196 5.53999 19.5196ZM15.93 2.90956C15.16 2.90956 14.49 3.38956 13.81 4.10956L5.59999 12.8096C5.39999 13.0196 5.16999 13.5196 5.12999 13.8096L4.75999 17.0496C4.71999 17.3796 4.79999 17.6496 4.97999 17.8196C5.15999 17.9896 5.42999 18.0496 5.75999 17.9996L8.97999 17.4496C9.26999 17.3996 9.74999 17.1396 9.94999 16.9296L18.16 8.23956C19.4 6.91956 19.85 5.69956 18.04 3.99956C17.24 3.22956 16.55 2.90956 15.93 2.90956Z" fill="var(--cv-text)"/>
                  <path d="M17.3399 10.9508C17.3199 10.9508 17.2899 10.9508 17.2699 10.9508C14.1499 10.6408 11.6399 8.27083 11.1599 5.17083C11.0999 4.76083 11.3799 4.38083 11.7899 4.31083C12.1999 4.25083 12.5799 4.53083 12.6499 4.94083C13.0299 7.36083 14.9899 9.22083 17.4299 9.46083C17.8399 9.50083 18.1399 9.87083 18.0999 10.2808C18.0499 10.6608 17.7199 10.9508 17.3399 10.9508Z" fill="var(--cv-text)"/>
                  <path d="M21 22.75H3C2.59 22.75 2.25 22.41 2.25 22C2.25 21.59 2.59 21.25 3 21.25H21C21.41 21.25 21.75 21.59 21.75 22C21.75 22.41 21.41 22.75 21 22.75Z" fill="var(--cv-text)"/>
                </svg>
                Umbenennen
              </button>

              {/* Divider */}
              <div style={{ height: 1, background: 'var(--cv-border)', margin: '4px 22px' }} />

              {/* Löschen */}
              <button
                style={MENU_ITEM(true)}
                onClick={() => { closeCtx(); onDelete && onDelete(ctxMenu.stelle); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047" stroke="var(--clr-error)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="var(--clr-error)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M18.85 9.14062L18.2 19.2106C18.09 20.7806 18 22.0006 15.21 22.0006H8.79002C6.00002 22.0006 5.91002 20.7806 5.80002 19.2106L5.15002 9.14062" stroke="var(--clr-error)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.33 16.5H13.66" stroke="var(--clr-error)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.5 12.5H14.5" stroke="var(--clr-error)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Löschen
              </button>
            </div>
          </>
        );
      })(), document.body)}

      {/* ── Header: Navis label top-left ── */}
      <div style={{
        padding: '0 24px',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
        paddingBottom: 10,
        flexShrink: 0,
      }}>
        <div style={{
          fontSize: 22, fontWeight: 800, letterSpacing: -0.5,
          color: th.text, fontFamily: "'Geist', -apple-system, sans-serif",
          userSelect: 'none',
        }}>
          Navis
        </div>
      </div>

      {/* ── Scrollable: Nav Items + Section header + Stellen ── */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>

        {/* Nav Items */}
        <div style={{ padding: '4px 12px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV_ITEMS.map(({ id, label, Icon }) => {
            const active = id === activeNav;
            return (
              <button
                key={id}
                onClick={() => onNavSelect(id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px', borderRadius: 16,
                  background: active ? '#004D40' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left', width: '100%',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'background 0.15s',
                }}
              >
                <Icon color={active ? '#F3EEE3' : 'rgba(28,24,18,0.4)'} />
                <span style={{
                  flex: 1,
                  fontSize: 17, fontWeight: active ? 600 : 400,
                  color: active ? '#F3EEE3' : th.text,
                  letterSpacing: -0.3,
                }}>
                  {label}
                </span>
              </button>
            );
          })}

          {/* Divider */}
          <div style={{ height: 1, background: th.border, margin: '6px 4px' }} />

          {/* Admin */}
          {rolle === 'admin' && (() => {
            const active = activeNav === 'admin';
            return (
              <button
                onClick={() => onNavSelect('admin')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px', borderRadius: 16,
                  background: active ? '#004D40' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left', width: '100%',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'background 0.15s',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2l3 7h7l-6 4.5 2.5 7.5L12 17l-6.5 4L8 13.5 2 9h7z"
                    stroke={active ? '#F3EEE3' : 'rgba(28,24,18,0.4)'} strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
                <span style={{
                  fontSize: 17, fontWeight: active ? 600 : 400,
                  color: active ? '#F3EEE3' : th.text,
                  letterSpacing: -0.3,
                }}>Admin</span>
              </button>
            );
          })()}
        </div>

        {/* Section header */}
        <div style={{ padding: '16px 24px 10px' }}>
          <span style={{
            fontSize: 10, fontFamily: th.mono,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: th.textDim, fontWeight: 700,
          }}>
            Aktuelle Stellen
          </span>
        </div>

        {/* Stellen */}
        {stellen.length === 0 ? (
          <div style={{ padding: '12px 24px', fontSize: 13, color: th.textMuted }}>
            Noch keine Stellen hinzugefügt.
          </div>
        ) : (
          <div style={{ padding: '0 10px 120px', display: 'flex', flexDirection: 'column' }}>
            {markierte.map(s => (
              <NavStelleCard key={s.id} s={s} markiert={true} th={th}
                onOpen={() => onStelleAuswaehlen(s)}
                isAktiv={s.id === aktiveStelleId}
                onContextMenu={openCtx}
                isRenaming={renamingId === s.id}
                onRenameSubmit={(stelle, neu) => {
                  setRenamingId(null);
                  if (neu !== stelle.name) onRename && onRename(stelle, neu);
                }} />
            ))}
            {restliche.map(s => (
              <NavStelleCard key={s.id} s={s} markiert={false} th={th}
                onOpen={() => onStelleAuswaehlen(s)}
                isAktiv={s.id === aktiveStelleId}
                onContextMenu={openCtx}
                isRenaming={renamingId === s.id}
                onRenameSubmit={(stelle, neu) => {
                  setRenamingId(null);
                  if (neu !== stelle.name) onRename && onRename(stelle, neu);
                }} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}


export default NavIsland;
export { NAV_ITEMS };
